import React, { useEffect, useLayoutEffect, useMemo } from 'react'
import { Banner, BannerLocations } from '../types'
import { Box, SxProps } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import { useBanner, useBannerSkeletonCount } from '../hooks'
import { useBannerContext } from '../provider'
import { BANNER_SESSION_STORAGE_PREFIX } from '../constants'
import BannerSkeleton from './BannerSkeleton'

interface BannerQueueProps {
  location: BannerLocations
  sx?: SxProps
}

/**
 * This component returns a queue of banners dependent on the banner array of the banner context
 */
const BannerQueue: React.FC<BannerQueueProps> = ({ location, sx }) => {
  const { warningBanners, infoBanners, addBanner, clearBanners } = useBannerContext()
  const { banners: initialBanners, isLoading: isBannersLoading } = useBanner()
  const { installationId } = useParams<{ installationId: string }>()
  const isStorageLoading = useTypedSelector(state => state.storage.storageLoading)
  const installation = useTypedSelector(state => state.storage.installation)
  const installationIdFromState = installation?.id.toString()
  const bannersInCurrentLocation = useMemo(() => {
    return initialBanners
      .filter(banner => banner.locations.includes(location))
      .filter(banner => !sessionStorage.getItem(`${BANNER_SESSION_STORAGE_PREFIX}${banner.key}`))
      .sort((bannerA, bannerB) => bannerB.priority - bannerA.priority)
  }, [initialBanners, location])
  const bannersToRender = useMemo(() => {
    const sortByPriority = (a: Banner, b: Banner): number => b.priority - a.priority

    const topWarningBanner = warningBanners
      .filter(b => b.locations.includes(location))
      .sort(sortByPriority)
      .at(0)
    const topInfoBanner = infoBanners
      .filter(b => b.locations.includes(location))
      .sort(sortByPriority)
      .at(0)

    return [topWarningBanner, topInfoBanner].filter(banner => banner !== undefined)
  }, [warningBanners, infoBanners, location])
  const hasActiveBanners = bannersToRender.length > 0

  const skeletonCount = useBannerSkeletonCount({
    location,
    bannersInCurrentLocation,
    isBannersLoading,
  })

  /**
   * Clear stale banners when the installation changes so that banners
   * from the previous installation are not shown.
   */
  useEffect(() => {
    clearBanners()
  }, [installationId, clearBanners])

  /**
   * The decision to share banners thought React context is wrong.
   * Too many re-renders are triggered because of that. The banners recreate every time you open new page.
   * The below useEffect is a workaround to avoid that problem.
   * It helps to avoid flickering when changing between pages.
   */
  useLayoutEffect(() => {
    for (const banner of bannersInCurrentLocation) {
      addBanner(banner)
    }
  }, [bannersInCurrentLocation, addBanner])

  /**
   * Because the state update isn't instant, there is a UI flicker (a banner displayed from the previously selected installation) when changing between installations.
   * To avoid that, we need the below condition to be checked.
   */
  const isInstallationSettling = isStorageLoading || (installationId && installationId !== installationIdFromState)

  if (!isInstallationSettling && hasActiveBanners) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', ...sx }}>
        {bannersToRender.map(banner => (
          <React.Fragment key={banner.key}>{banner.content}</React.Fragment>
        ))}
      </Box>
    )
  }

  if ((isInstallationSettling || isBannersLoading) && skeletonCount > 0) {
    return (
      <Box sx={{ width: '100%', gap: 2, display: 'flex', flexDirection: 'column', ...sx }}>
        {Array.from({ length: skeletonCount }, (_, index) => (
          <BannerSkeleton key={index} />
        ))}
      </Box>
    )
  }

  return null
}

export default BannerQueue
